import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setEmailCode } from '../redux/actions/Actions';
import { assets } from '../../assets/assets';

const ForgotPassword = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const [email,   setEmail]   = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateEmail(email)) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    try {
      const response = await dispatch(setEmailCode(email));
      if (response && response.status >= 400) {
        setError(response.error?.message || 'An error occurred.');
      } else {
        navigate('/verificationcode', { state: { email } });
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.page}>

      {/* ── Left panel ── */}
      <div style={s.left}>
        <div style={s.leftInner}>
          <img src={assets.logo_img} alt="UPL" style={s.logo} />
          <h2 style={s.leftH}>Reset Your Password</h2>
          <p style={s.leftP}>
            No worries — it happens to everyone. Enter your registered email and
            we'll send you a verification code to reset your password securely.
          </p>
          <div style={s.steps}>
            {[
              { n: '01', t: 'Enter your email address' },
              { n: '02', t: 'Receive a verification code' },
              { n: '03', t: 'Set your new password'      },
            ].map((st) => (
              <div key={st.n} style={s.step}>
                <div style={s.stepNum}>{st.n}</div>
                <div style={s.stepText}>{st.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={s.right}>
        <div style={s.card}>
          <Link to="/login" style={s.close}>✕</Link>

          {/* Icon */}
          <div style={s.iconWrap}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>

          <h1 style={s.heading}>Forgot Password?</h1>
          <p style={s.sub}>
            Enter the email address associated with your account and we'll send you a code.
          </p>

          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.field}>
              <label style={s.label}>Email Address</label>
              <div style={s.inputWrap}>
                <svg style={s.inputIcon} viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input
                  type="email" value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="Enter your registered email"
                  style={s.input} autoComplete="email"
                  onFocus={e => e.target.parentNode.style.borderColor = '#2563EB'}
                  onBlur={e  => e.target.parentNode.style.borderColor = '#CBD5E1'}
                />
              </div>
            </div>

            {error && (
              <div style={s.errorBox}>{error}</div>
            )}

            <button type="submit" style={{ ...s.btn, opacity: loading ? 0.7 : 1 }}
              disabled={loading}
              onMouseEnter={e => !loading && (e.target.style.background = '#1D4ED8')}
              onMouseLeave={e => !loading && (e.target.style.background = '#2563EB')}>
              {loading ? 'Sending...' : 'GET VERIFICATION CODE'}
            </button>
          </form>

          <div style={{ borderTop: '1px solid #E2E8F0', margin: '20px 0 16px' }} />

          <p style={s.footer}>
            Remember your password?{' '}
            <Link to="/login" style={s.link}>Sign in</Link>
          </p>
          <p style={s.footer}>
            Don't have an account?{' '}
            <Link to="/registerform" style={s.link}>Sign up for free</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const s = {
  page: {
    minHeight:           '100vh',
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    fontFamily:          "'Inter','Segoe UI',sans-serif",
  },

  /* Left */
  left: {
    background:     'linear-gradient(160deg, #0D9488 0%, #0EA5E9 35%, #2563EB 70%, #1D4ED8 100%)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '60px 8%',
  },
  leftInner: { maxWidth: 420 },
  logo:      { height: 48, marginBottom: 40, filter: 'brightness(0) invert(1)' },
  leftH:     { fontSize: 'clamp(28px,3.5vw,44px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.15, margin: '0 0 18px' },
  leftP:     { fontSize: 16, color: 'rgba(255,255,255,0.82)', lineHeight: 1.7, marginBottom: 40 },

  steps: { display: 'flex', flexDirection: 'column', gap: 18 },
  step:  { display: 'flex', alignItems: 'center', gap: 16 },
  stepNum: {
    width: 38, height: 38, borderRadius: '50%',
    background: 'rgba(255,255,255,0.18)', border: '1.5px solid rgba(255,255,255,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0, letterSpacing: '0.04em',
  },
  stepText: { fontSize: 15, color: 'rgba(255,255,255,0.88)', fontWeight: 500 },

  /* Right */
  right: {
    background:     '#F8FAFC',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '40px 32px',
  },
  card: {
    background:    '#FFFFFF',
    borderRadius:  '16px',
    border:        '1px solid #E2E8F0',
    boxShadow:     '0 4px 24px rgba(0,0,0,0.07)',
    padding:       '44px 40px 36px',
    width:         '100%',
    maxWidth:      '440px',
    position:      'relative',
  },
  close: { position: 'absolute', top: 16, right: 20, fontSize: 20, color: '#64748B', textDecoration: 'none', lineHeight: 1 },

  iconWrap: {
    width: 56, height: 56, borderRadius: 14,
    background: '#EFF6FF', border: '1px solid #BFDBFE',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 20,
  },
  heading: { fontSize: 26, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', margin: '0 0 10px' },
  sub:     { fontSize: 14, color: '#64748B', lineHeight: 1.65, marginBottom: 28 },

  form:  { display: 'flex', flexDirection: 'column', gap: 18 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 700, color: '#334155' },

  inputWrap: {
    display: 'flex', alignItems: 'center',
    border: '1.5px solid #CBD5E1', borderRadius: '8px',
    overflow: 'hidden', transition: 'border-color 0.2s', background: '#FFFFFF',
  },
  inputIcon: { width: 18, height: 18, marginLeft: 14, flexShrink: 0 },
  input: {
    flex: 1, padding: '13px 14px', border: 'none', outline: 'none',
    fontSize: '15px', color: '#1E293B', background: 'transparent', width: '100%',
  },

  errorBox: {
    background: '#FEF2F2', border: '1px solid #FCA5A5',
    borderRadius: 8, padding: '10px 14px',
    color: '#B91C1C', fontSize: 13.5,
  },

  btn: {
    padding: '15px', background: '#2563EB', color: '#FFFFFF',
    border: 'none', borderRadius: '8px', fontSize: '14px',
    fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer',
    transition: 'background 0.2s', width: '100%',
  },

  footer: { fontSize: 13.5, color: '#64748B', textAlign: 'center', margin: '6px 0 0' },
  link:   { color: '#2563EB', textDecoration: 'none', fontWeight: 600 },
};

export default ForgotPassword;
